import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { FieldDef } from './use-cheki-templates'
import type { Json } from '@/types/supabase'
import { triggerRevalidation } from '@/lib/utils'

export type { FieldDef } from './use-cheki-templates'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ChekiFormMember {
  id: string
  form_id: string
  member_id: string
  quota: number      // PO quota
  quota_ots: number  // OTS quota, 0 = unlimited
  booked_count: number
  idol_members: { name: string; stage_name: string | null; photo_url: string | null } | null
}

export interface ChekiForm {
  id: string
  tenant_id: string
  event_id: string
  title: string
  opens_at: string
  closes_at: string
  quota_type: 'group' | 'per_member'
  total_quota: number | null      // PO quota for group mode, null = unlimited
  total_quota_ots: number         // OTS quota for group mode, 0 = unlimited
  booked_count: number
  price_idr: number
  price_ots_idr: number | null
  group_price_idr: number | null
  group_price_ots_idr: number | null
  group_cheki_quota: number | null     // PO quota for group cheki, null = unlimited
  group_cheki_quota_ots: number        // OTS quota for group cheki, 0 = unlimited
  group_cheki_booked_count: number
  status: 'draft' | 'open' | 'closed'
  created_at: string
  updated_at: string
  events: {
    title: string
    starts_at: string
    event_members?: Array<{
      member_id: string
      idol_members: { name: string; stage_name: string | null; photo_url: string | null } | null
    }>
  } | null
  template_id: string | null
  custom_fields: FieldDef[]
  order_count?: number
  submission_count?: number
  cheki_form_members?: ChekiFormMember[]
}

export interface ChekiOrder {
  id: string
  form_id: string
  tenant_id: string
  fan_name: string
  fan_email: string
  fan_phone: string
  pickup_method: 'diambil_ditempat' | 'titip_member' | 'titip_teman'
  member_id: string | null
  proof_url: string | null
  booking_code: string
  invoice_code: string | null
  quantity: number
  session_id: string | null
  status: 'pending' | 'proof_uploaded' | 'verified' | 'rejected' | 'cancelled'
  pickup_status: 'not_taken' | 'taken'
  admin_note: string | null
  verified_by: string | null
  verified_at: string | null
  created_at: string
  custom_data: Record<string, unknown>
  idol_members: { name: string; stage_name: string | null } | null
  is_group_cheki: boolean
  is_ots: boolean
  ots_session_id: string | null
  payment_method: 'cash' | 'qris' | 'transfer' | null
}

export type ChekiOrderGlobal = ChekiOrder & {
  cheki_forms: {
    id: string
    title: string
    status: 'draft' | 'open' | 'closed'
    price_idr: number
    price_ots_idr: number | null
    group_price_idr: number | null
    group_price_ots_idr: number | null
  }
}

export interface KasirOTSSession {
  id: string
  form_id: string
  tenant_id: string
  status: 'open' | 'closed'
  opened_at: string | null
  closed_at: string | null
  opened_by: string | null
  closed_by: string | null
  created_at: string
  updated_at: string
}

export interface CreateOTSOrderInput {
  form_id: string
  tenant_id: string
  ots_session_id: string
  fan_name?: string
  fan_phone?: string
  member_id?: string | null
  quantity: number
  payment_method: 'cash' | 'qris' | 'transfer'
  is_group_cheki?: boolean
  price_ots_idr: number
  custom_data?: Record<string, unknown>
}

export interface CreateChekiFormInput {
  event_id: string
  title: string
  opens_at: string
  closes_at: string
  quota_type: 'group' | 'per_member'
  total_quota?: number | null
  total_quota_ots?: number
  price_idr: number
  price_ots_idr?: number | null
  group_price_idr?: number | null
  group_price_ots_idr?: number | null
  group_cheki_quota?: number | null
  group_cheki_quota_ots?: number
  status: 'draft' | 'open' | 'closed'
  template_id?: string | null
  custom_fields: FieldDef[]
  members?: Array<{ member_id: string; quota: number; quota_ots: number }>
}

export interface UpdateChekiFormInput extends CreateChekiFormInput {
  id: string
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useChekiForms() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['cheki-forms', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cheki_forms')
        .select(`
          *,
          events(title, starts_at, event_members(member_id, idol_members(name, stage_name, photo_url))),
          cheki_form_members(member_id, quota, booked_count, idol_members(name, stage_name, photo_url)),
          cheki_orders(id, session_id, custom_data)
        `)
        .eq('tenant_id', tenantId!)
        .order('opens_at', { ascending: false })

      if (error) throw error

      type FRow = {
        cheki_orders?: Array<{ id: string; session_id: string | null; custom_data: Record<string, unknown> | null }>
        [key: string]: unknown
      }
      return ((data ?? []) as unknown as FRow[]).map((f) => {
        const orders = f.cheki_orders ?? []
        const submissionSet = new Set(
          orders.map((o) => o.session_id ?? (o.custom_data?.transaction_group_id as string | undefined) ?? o.id)
        )
        return {
          ...f,
          order_count: orders.length,
          submission_count: submissionSet.size,
        }
      }) as unknown as ChekiForm[]
    },
  })
}

export function useChekiForm(formId: string | undefined) {
  return useQuery({
    queryKey: ['cheki-form', formId],
    enabled: !!formId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cheki_forms')
        .select(`
          *,
          events(title, starts_at, event_members(member_id, idol_members(name, stage_name, photo_url))),
          cheki_form_members(*, idol_members(name, stage_name, photo_url))
        `)
        .eq('id', formId!)
        .single()

      if (error) throw error
      return data as unknown as ChekiForm
    },
  })
}

export function useChekiOrders(formId: string | undefined) {
  return useQuery({
    queryKey: ['cheki-orders', formId],
    enabled: !!formId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cheki_orders')
        .select('*, idol_members(name, stage_name)')
        .eq('form_id', formId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as ChekiOrder[]
    },
  })
}

export function useAllChekiOrders() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  return useQuery({
    queryKey: ['cheki-orders-all', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cheki_orders')
        .select(`
          *,
          idol_members(name, stage_name),
          cheki_forms!inner(id, title, status, tenant_id,
            price_idr, price_ots_idr, group_price_idr, group_price_ots_idr)
        `)
        .eq('cheki_forms.tenant_id', tenantId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as unknown as ChekiOrderGlobal[]
    },
  })
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateChekiForm() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: CreateChekiFormInput) => {
      const { members, ...formFields } = input

      // 1. Insert form
      const { data: form, error: formErr } = await supabase
        .from('cheki_forms')
        .insert({ ...formFields, custom_fields: formFields.custom_fields as unknown as Json, tenant_id: tenantId! })
        .select()
        .single()

      if (formErr) throw formErr

      // 2. Insert per-member quota rows if per_member
      if (input.quota_type === 'per_member' && members?.length) {
        const memberRows = members.map((m) => ({
          form_id: form.id,
          member_id: m.member_id,
          quota: m.quota,
          quota_ots: m.quota_ots ?? 0,
        }))
        const { error: membErr } = await supabase
          .from('cheki_form_members')
          .insert(memberRows)
        if (membErr) throw membErr
      }

      return form
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cheki-forms', tenantId] }),
  })
}

export function useUpdateChekiForm() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, members, ...fields }: UpdateChekiFormInput) => {
      // 1. Update form fields
      const { error: formErr } = await supabase
        .from('cheki_forms')
        .update({ ...fields, custom_fields: fields.custom_fields as unknown as Json })
        .eq('id', id)

      if (formErr) throw formErr

      // 2. Sync per-member rows: delete all then re-insert
      const { error: delErr } = await supabase
        .from('cheki_form_members')
        .delete()
        .eq('form_id', id)

      if (delErr) throw delErr

      if (fields.quota_type === 'per_member' && members?.length) {
        const memberRows = members.map((m) => ({
          form_id: id,
          member_id: m.member_id,
          quota: m.quota,
          quota_ots: m.quota_ots ?? 0,
        }))
        const { error: insErr } = await supabase
          .from('cheki_form_members')
          .insert(memberRows)
        if (insErr) throw insErr
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cheki-forms', tenantId] })
      qc.invalidateQueries({ queryKey: ['cheki-form', vars.id] })
    },
  })
}

export function useDeleteChekiForm() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('cheki_forms').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cheki-forms', tenantId] }),
  })
}

export function useVerifyChekiOrder() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      formId: _formId,
      status,
      admin_note,
    }: {
      id: string
      formId: string
      status: 'verified' | 'rejected'
      admin_note?: string
    }) => {
      const { error } = await supabase
        .from('cheki_orders')
        .update({
          status,
          admin_note: admin_note ?? null,
          verified_at: new Date().toISOString(),
        })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cheki-orders', vars.formId] })
      qc.invalidateQueries({ queryKey: ['cheki-form', vars.formId] })
    },
  })
}

export interface MemberChekiOrder {
  id: string
  fan_name: string
  fan_email: string
  fan_phone: string
  booking_code: string
  quantity: number
  status: string
  created_at: string
  cheki_forms: {
    title: string
    events: { title: string; starts_at: string } | null
  } | null
}

export function useToggleChekiPickup() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      formId: _formId,
      pickup_status,
    }: {
      id: string
      formId: string
      pickup_status: 'not_taken' | 'taken'
    }) => {
      const { error } = await supabase
        .from('cheki_orders')
        .update({ pickup_status })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cheki-orders', vars.formId] })
    },
  })
}

export function useBatchVerifyChekiOrders() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ids,
      formId: _formId,
      status,
      admin_note,
    }: {
      ids: string[]
      formId: string
      status: 'verified' | 'rejected'
      admin_note?: string
    }) => {
      const { error } = await supabase
        .from('cheki_orders')
        .update({
          status,
          admin_note: admin_note ?? null,
          verified_at: new Date().toISOString(),
        })
        .in('id', ids)

      if (error) throw error
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cheki-orders', vars.formId] })
      qc.invalidateQueries({ queryKey: ['cheki-form', vars.formId] })
    },
  })
}

export function useBatchToggleChekiPickup() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ids,
      formId: _formId,
      pickup_status,
    }: {
      ids: string[]
      formId: string
      pickup_status: 'not_taken' | 'taken'
    }) => {
      const { error } = await supabase
        .from('cheki_orders')
        .update({ pickup_status })
        .in('id', ids)
      if (error) throw error
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cheki-orders', vars.formId] })
    },
  })
}

export function useChekiOrdersByMember(memberId: string | null) {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['cheki-orders-by-member', memberId, tenantId],
    enabled: !!memberId && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cheki_orders')
        .select(`
          id, fan_name, fan_email, fan_phone, booking_code,
          quantity, status, created_at,
          cheki_forms(title, events(title, starts_at))
        `)
        .eq('member_id', memberId!)
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as MemberChekiOrder[]
    },
  })
}

// ── Kasir OTS ─────────────────────────────────────────────────────────────────

export function useKasirOTSSession(formId: string | undefined) {
  return useQuery({
    queryKey: ['kasir-ots-session', formId],
    enabled: !!formId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('kasir_ots_sessions')
        .select('*') as any)
        .eq('form_id', formId!)
        .maybeSingle()
      if (error) throw error
      return data as KasirOTSSession | null
    },
  })
}

export function useOpenKasirSession() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const user = useAuthStore((s) => s.user)

  return useMutation({
    mutationFn: async (formId: string) => {
      const now = new Date().toISOString()
      const { data, error } = await (supabase
        .from('kasir_ots_sessions')
        .upsert(
          {
            form_id: formId,
            tenant_id: tenantId!,
            status: 'open',
            opened_at: now,
            closed_at: null,
            opened_by: user?.id ?? null,
          },
          { onConflict: 'form_id' }
        )
        .select()
        .single() as any)
      if (error) throw error
      return data as KasirOTSSession
    },
    onSuccess: (_data, formId) => {
      qc.invalidateQueries({ queryKey: ['kasir-ots-session', formId] })
    },
  })
}

export function useCloseKasirSession() {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  return useMutation({
    mutationFn: async ({ sessionId, formId: _formId }: { sessionId: string; formId: string }) => {
      const { error } = await (supabase
        .from('kasir_ots_sessions')
        .update({
          status: 'closed',
          closed_at: new Date().toISOString(),
          closed_by: user?.id ?? null,
        })
        .eq('id', sessionId) as any)
      if (error) throw error
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['kasir-ots-session', vars.formId] })
    },
  })
}

function generateOTSCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = 'OTS-'
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

export function useCreateOTSOrder() {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  return useMutation({
    mutationFn: async (input: CreateOTSOrderInput) => {
      const now = new Date().toISOString()
      const { data, error } = await (supabase
        .from('cheki_orders')
        .insert({
          form_id: input.form_id,
          tenant_id: input.tenant_id,
          fan_name: input.fan_name || 'OTS',
          fan_email: '',
          fan_phone: input.fan_phone || '',
          pickup_method: 'diambil_ditempat',
          member_id: input.member_id ?? null,
          quantity: input.quantity,
          booking_code: generateOTSCode(),
          status: 'verified',
          pickup_status: 'taken',
          verified_at: now,
          verified_by: user?.id ?? null,
          is_ots: true,
          ots_session_id: input.ots_session_id,
          payment_method: input.payment_method,
          is_group_cheki: input.is_group_cheki ?? false,
          custom_data: (input.custom_data ?? {}) as unknown as Json,
        })
        .select()
        .single() as any)
      if (error) throw error
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cheki-orders', vars.form_id] })
      qc.invalidateQueries({ queryKey: ['cheki-form', vars.form_id] })
    },
  })
}

// ── Tenant Cheki Default Prices ───────────────────────────────────────────────

export interface ChekiDefaultPrices {
  default_price_idr: number | null
  default_price_ots_idr: number | null
  default_group_price_idr: number | null
  default_group_price_ots_idr: number | null
}

export function useTenantChekiDefaultPrices() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['tenant-cheki-default-prices', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('tenants')
        .select('default_price_idr, default_price_ots_idr, default_group_price_idr, default_group_price_ots_idr') as any)
        .eq('id', tenantId!)
        .single()
      if (error) throw error
      return data as ChekiDefaultPrices
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: ChekiDefaultPrices) => {
      const { error } = await (supabase
        .from('tenants')
        .update(values as any) as any)
        .eq('id', tenantId!)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-cheki-default-prices', tenantId] })
    },
  })

  return { query, mutation }
}

// ── Tenant Cheki Group Setting ─────────────────────────────────────────────────

export function useTenantChekiGroupSetting() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['tenant-cheki-group', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('tenants')
        .select('has_cheki_group') as any)
        .eq('id', tenantId!)
        .single()
      if (error) throw error
      return data as { has_cheki_group: boolean }
    },
  })

  const mutation = useMutation({
    mutationFn: async (value: boolean) => {
      const { error } = await (supabase
        .from('tenants')
        .update({ has_cheki_group: value } as any) as any)
        .eq('id', tenantId!)
      if (error) throw error
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant-cheki-group', tenantId] })
      await triggerRevalidation('tenant-theme')
    },
  })

  return { query, mutation }
}
